<template>
    <div>
      <AdminHeader />
  
      <div class="page-header">
        <div>
          <h5 class="page-title">Role List</h5>
          <p class="page-sub">Define roles and control what each role can access</p>
        </div>
        <div class="page-actions">
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search roles..." v-model="tableData.search" @input="tableData.page = 1; fetchData()" />
          </div>
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'l', 'roles', 'Roles')"><font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF</a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('roles.csv')"><font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV</a></li>
            </ul>
          </div>
          <router-link :to="$url('/create-role')" class="btn-primary-action" :class="permission?.create_role == 0 ? 'disabled' : ''">
            <font-awesome-icon icon="plus" /> Add Role
          </router-link>
        </div>
      </div>
  
      <div class="data-card">
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading roles...</p>
        </div>
  
        <!-- No Permission -->
        <div v-else-if="permission_status == 403 && !is_permission" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to access this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--purple"><font-awesome-icon icon="user-shield" /></div>
          <p class="state-title">No roles yet</p>
          <p class="state-sub">Create your first role to start managing access.</p>
          <router-link :to="$url('/create-role')" class="btn-primary-action mt-2" v-if="permission?.create_role != 0">
            <font-awesome-icon icon="plus" /> Add Role
          </router-link>
        </div>
  
        <!-- Table -->
        <template v-else>
          <div class="table-toolbar">
            <span class="table-count">Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> roles</span>
            <div class="d-flex align-items-center gap-2">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check"><input type="checkbox" v-model="allSelected" @change="selectAll" class="form-check-input" /></th>
                  <th>Role Name</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(role, index) in details" :key="index" :class="selectedModel.includes(role.id) ? 'row-selected' : ''">
                  <td class="col-check"><input type="checkbox" v-model="selectedModel" :value="role.id" class="form-check-input" /></td>
                  <td>
                    <div class="role-cell">
                      <div class="role-avatar"><font-awesome-icon icon="user-shield" /></div>
                      <span class="role-name">{{ role.name }}</span>
                    </div>
                  </td>
                  <td class="col-actions">
                    <div class="action-btns">
                      <router-link
                        :to="(permission?.edit_role == 0 || (role.slug === 'business_owner' && !isSuperAdmin)) ? '#' : { name: 'Edit Role', params: { companyCode: $route.params.companyCode, id: role.id } }"
                        class="action-btn action-btn--edit"
                        :class="(permission?.edit_role == 0 || (role.slug === 'business_owner' && !isSuperAdmin)) ? 'disabled' : ''"
                        :title="role.slug === 'business_owner' && !isSuperAdmin ? 'Only super admin can edit this role' : 'Edit role'"
                      >
                        <font-awesome-icon icon="pen" />
                      </router-link>
                      <button
                        class="action-btn action-btn--delete"
                        @click="getModalCurrentId(role.id)"
                        :data-bs-toggle="company?.user_id !== role.id && permission?.delete_role == 1 ? 'modal' : ''"
                        :data-bs-target="company?.user_id !== role.id && permission?.delete_role == 1 ? '#singleModal' : ''"
                        :disabled="company?.user_id === role.id || permission?.delete_role != 1"
                        title="Delete role"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
        </template>
      </div>
  
      <SingleDeleteModal :modalId="'singleModal'" @deleteSingle="callSingleRoleDeleteButton" :modalText="'Are you sure you want to delete this role?'" :action="'deleteSingle'" />
    </div>
  </template>
  
  <script>
  import AdminHeader from "./administrationheadertab.vue";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import SingleDeleteModal from '../../../components/ActionModal.vue';
  import { reusableMixin } from "../../../lib/reusableMixin";
  import { useStore } from "vuex";
  
  export default {
    components: { AdminHeader, SingleDeleteModal },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content: [], headerArrayColumn: ['S/N', 'Role Name'],
        details: [], loading: false, query: null,
        selectedModel: [], allSelected: false,
        tableData: { page: 1, draw: 0, length: 5, search: '', column: 0, dir: 'desc', id: '', status: [0,1,2,3], query: '' },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), currentID: null, company: null,
        permission_status: null, is_permission: true, store: useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
      isSuperAdmin() { return this.store.state.getLoggedInUser?.is_super === 1; },
    },
  
    watch: { query() { this.fetchData(); } },
    created() { this.fetchData(); this.getUserCompany(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((r, idx) => ({ sn: idx + 1, role: r.name }));
      },
      getUserCompany() {
        axios.get('/settings/company').then(r => { this.company = r.data.data.data; });
      },
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },
      fetchData(url = '/admin/role') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(r => {
            this.loading = false;
            this.details = r.data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(r.data.data);
          })
          .catch(e => {
            this.loading = false; this.is_permission = false;
            if (e?.response?.status == 403) { this.permission_status = 403; this.toast.error(e.response.data.message); }
          });
      },
      callSingleRoleDeleteButton() {
        axios.delete(`/admin/role/${this.currentID}`)
          .then(() => { this.toast.success('Role deleted successfully'); this.fetchData(); })
          .catch(e => { this.toast.error(e?.response?.data?.message ?? 'Something went wrong'); });
      },
      getModalCurrentId(id) { this.currentID = id; },
      configPagination(data) {
        this.pagination.lastPage = data.last_page; this.pagination.currentPage = data.current_page;
        this.pagination.total = data.total; this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url; this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from = data.from; this.pagination.to = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  .search-wrap { position:relative; display:flex; align-items:center; }
  .search-icon { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:220px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
  .state-icon--red { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub { font-size:13px; color:#7a90b0; margin:0; }
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count { font-size:13px; color:#7a90b0; }
  .rows-select { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  .table-wrap { overflow-x:auto; }
  .data-table { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody tr.row-selected { background:#edf4ff; }
  .data-table tbody td { padding:13px 16px; font-size:13.5px; vertical-align:middle; }
  .col-check { width:44px; text-align:center; }
  .col-actions { width:90px; text-align:center; }
  .role-cell { display:flex; align-items:center; gap:10px; }
  .role-avatar { width:34px; height:34px; border-radius:8px; background:rgba(124,58,237,.1); color:#7c3aed; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
  .role-name { font-weight:600; color:#1a2b45; text-transform:capitalize; }
  .action-btns { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s,border-color .15s; text-decoration:none; background:#fff; }
  .action-btn--edit { color:#004AAD; }
  .action-btn--edit:hover:not(.disabled) { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete { color:#e03535; }
  .action-btn--delete:hover:not(:disabled) { background:#fff0f0; border-color:#fac5c5; }
  .action-btn--delete:disabled { color:#ccc; cursor:not-allowed; }
  .action-btn.disabled { color:#ccc; pointer-events:none; }
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
  }
  </style>